<template>
  <component-wrapper class="overview-teasers-fi">
    <grid-container-fi :container="false">
      <div class="fi-col-span-full">
        <tab-navigation-fi
          v-if="activateFilters"
          class="overview-teasers-fi__tab-navigation"
          :tab-navigation="tabNavigation"
          :is-slider="true"
          @change="(value) => tabChanged(value)"
        />
      </div>
    </grid-container-fi>
    <grid-container-fi>
      <div class="fi-col-start-1 fi-col-end-13 md:fi-col-start-2 md:fi-col-end-12">
        <!-- eslint-disable vue/no-v-html -->
        <h3
          v-if="isEditMode"
          class="overview-teasers-fi__headline"
          v-html="fields.headline.editable"
        />
        <h3
          v-if="!isEditMode && fields.headline?.value"
          class="overview-teasers-fi__headline"
          v-html="fields.headline.value"
        />
        <!-- eslint-enable vue/no-v-html -->
      </div>
    </grid-container-fi>
    <grid-container-fi :container="false">
      <div class="fi-col-span-full">
        <transition
          enter-active-class="animated fadeIn fastest"
          leave-active-class="animated fadeOut fastest"
          mode="out-in"
        >
          <div
            v-if="teaserList"
            :key="currentTag ? currentTag.id : 0"
          >
            <div class="overview-teasers-fi__teasers">
              <loading-indicator
                v-if="isLoading"
                variant="spinnerLocal"
                class="spinner-local--rebrush"
                :dark="true"
              />

              <template v-for="(teaser, index) in teaserList">
                <teaser-big-image-element-fi
                  v-if="teaser.Document"
                  :key="teaser.Document.ItemLink"
                  :image="
                    teaser.Document.Thumbnail
                      ? { src: teaser.Document.Thumbnail, alt: teaser.Document.TeaserTitle }
                      : null
                  "
                  :image-pos-right="index % 2 !== 0"
                  bg-color="light"
                >
                  <template #content>
                    <template v-if="teaser.Document.TagNames">
                      <span
                        v-for="tag in teaser.Document.TagNames.split('|')"
                        :key="tag"
                        class="badge-fi"
                      >
                        {{ tag }}
                      </span>
                    </template>
                    <h3 v-if="teaser.Document.TeaserTitle">
                      {{ teaser.Document.TeaserTitle }}
                    </h3>
                    <p v-if="teaser.Document.PreviewText">
                      {{ teaser.Document.PreviewText }}
                    </p>
                    <button-fi
                      v-if="teaser.Document.ItemLink && fields.teaserButtonText?.value"
                      :href="teaser.Document.ItemLink"
                      class="button-fi__tertiary"
                      natural-events
                    >
                      {{ fields.teaserButtonText.value }}
                    </button-fi>
                  </template>
                </teaser-big-image-element-fi>
              </template>
            </div>

            <section-divider-fi />

            <pagination-progress-bar
              :pagination-page="page + 1"
              :pagination-total="teaserCount"
              :progressbar-value="pagingPercentage"
              :disabled-state="isLoading"
              :show-more-button="teaserList.length < teaserCount"
              :query-params="null"
              @loadmore="fetchMore"
            >
              <template v-if="(page + 1) * pageSize <= teaserCount">
                <span>{{ loadMoreDict((page + 1) * pageSize, teaserCount) }}</span>
              </template>

              <template v-else>
                <span>{{ loadMoreDict(teaserCount, teaserCount) }}</span>
              </template>
            </pagination-progress-bar>

            <section-divider-fi />
          </div>
        </transition>
      </div>
    </grid-container-fi>
  </component-wrapper>
</template>

<script>
import { ButtonFi, LoadingIndicator, PaginationProgressBar } from 'atoms';
import axios from 'axios';
import ComponentWrapper from 'components/component-wrapper/ComponentWrapper';
import GridContainerFi from 'components/grid-fi/GridContainerFi';
import TabNavigationFi from 'components/tab-navigation-fi/TabNavigationFi';
import TeaserBigImageElementFi from 'components/teaser/teaser-big-image-element-fi/TeaserBigImageElementFi';
import SectionDividerFi from 'pages/section-divider-fi/SectionDividerFi';

export default {
  name: 'OverviewTeasersFi',
  components: {
    ButtonFi,
    LoadingIndicator,
    ComponentWrapper,
    GridContainerFi,
    SectionDividerFi,
    TabNavigationFi,
    TeaserBigImageElementFi,
    PaginationProgressBar,
  },
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    apiUrl: {
      type: String,
      default: null,
      required: true,
    },
    customType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isLoading: true,
      currentTag: null,
      teaserList: [],
      teaserCount: 0,
      page: 0,
      pageSize: 9,
      filtered: false,
    };
  },
  computed: {
    type() {
      return this.customType || this.fields?.type?.value;
    },
    allTag() {
      return {
        id: 0,
        text: this.fields?.allTagsLabel?.value,
      };
    },
    tags() {
      if (this.fields?.tags?.length) {
        return this.fields.tags.filter(
          (tag) => tag.fields?.tagName?.value && tag.fields.tagName.value !== '',
        );
      }

      return [];
    },
    tagsLogicalOr() {
      return Boolean(this.fields?.tagsLogicalOr?.value);
    },
    activateFilters() {
      return Boolean(this.fields?.activateFilters?.value && this.tabNavigation);
    },
    tabNavigation() {
      if (this.tags.length) {
        const navigation = this.tags.map((tag, index) => ({
          id: index + 1,
          text: tag.fields?.tagName?.value,
          tagId: tag.id,
        }));

        return [this.allTag, ...navigation];
      }

      return null;
    },
    activeTag() {
      if (this.currentTag) {
        return this.currentTag;
      }

      return this.allTag;
    },
    pagingPercentage() {
      return (100 * this.teaserList.length) / this.teaserCount;
    },
    fetchParams() {
      const pageid = this.fields?.usePageId?.value ? this.$fischer.pageId() : null;
      const lang = this.$fischer.siteLanguage() || null;
      const type = this.type !== this.customType ? this.type : null;
      const email = this.customType === 'NewsPage' ? this.$fischer.user.email() : null;
      let tags = null;

      if (this.activeTag.id === 0 && this.tags?.length) {
        tags = this.tags.map((tag) => tag.id).join('|');
      } else if (this.activeTag.id !== 0) {
        tags = this.activeTag.tagId;
      }

      const params = {
        pageid,
        type,
        lang,
        tags,
        tagslogicalor: this.tagsLogicalOr,
        email,
        page: this.page,
        pageSize: this.pageSize,
      };

      return params;
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    tabChanged(value) {
      this.isLoading = true;
      this.currentTag = value;
      this.filtered = value.id !== 0;
      this.fetchData();
    },
    fetchData() {
      axios
        .get(this.apiUrl, {
          params: this.fetchParams,
        })
        .then((result) => {
          if (result.data?.Data) {
            this.teaserCount = result.data.Data.TotalSearchResults;
            this.teaserList = result.data.Data.TeaserList;
          }
          this.isLoading = false;
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
        });
    },
    fetchMore() {
      this.page += 1;

      axios
        .get(this.apiUrl, {
          params: this.fetchParams,
        })
        .then((result) => {
          if (result.data?.Data) {
            this.teaserList = [...this.teaserList, ...result.data.Data.TeaserList];
          }
          this.isLoading = false;
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
        });
    },
    loadMoreDict(first, second) {
      let dict = null;

      switch (this.type) {
        case 'StoryPage':
          dict = this.$dict.get('teaser-Overview.Pagination.Show-Stories', [first, second]);
          break;
        case 'ReferencePage':
          dict = this.$dict.get('teaser-Overview.Pagination.Show-References', [first, second]);
          break;
        case 'NewsPage':
          dict = this.$dict.get('teaser-Overview.Pagination.Show-News', [first, second]);
          break;
        default:
          dict = this.$dict.get('teaser-Overview.Pagination.Default', [first, second]);
      }

      return dict;
    },
  },
};
</script>

<style scoped lang="scss">
@import './overview-teasers-fi';
</style>
